import { CampaignCategory, CampaignSnsRequirementSnsType, LanguageType } from '~/generated/graphql'
import { getDefaultTags } from '~/lib/types/Campaign'
import { SUPPORTED_TAG_LANGUAGES, getSnsTagLimit, SupportedTagLanguage } from '~/lib/sns'
import { createSnsRequirementDefaultValues } from './state'

type SnsRequirements = ReturnType<typeof createSnsRequirementDefaultValues>

/**
 * カテゴリーとサブジャンルIDに基づいてデフォルトハッシュタグを生成する
 *
 * @param snsRequirements 現在のSNS要件
 * @param category キャンペーンカテゴリー
 * @param searchSubGenreIds サブジャンルIDの配列
 * @param desiredLanguages 希望言語の配列
 * @returns 更新されたSNS要件（タグが空の場合のみデフォルトタグを生成）
 */
export function updateTagsWithDefaults(
  snsRequirements: SnsRequirements,
  category: CampaignCategory,
  searchSubGenreIds: number[],
  desiredLanguages: LanguageType[],
) {
  // サブジャンルが指定されていない、または投稿希望言語が指定されていない場合は、現在のタグをそのまま返す
  if (searchSubGenreIds.length === 0 || desiredLanguages.length === 0) {
    return snsRequirements
  }

  // 全てのSNSのタグが空かどうかをチェック
  const isAllTagsEmpty =
    snsRequirements.INSTAGRAM.tagList.every((tl) => tl.tags.filter((tag) => tag.trim() !== '').length === 0) &&
    snsRequirements.TIKTOK.tagList.every((tl) => tl.tags.filter((tag) => tag.trim() !== '').length === 0)

  // タグが既に存在する場合、現在のタグをそのまま返す
  if (!isAllTagsEmpty) {
    return snsRequirements
  }

  // サポートされている言語のみをフィルタリング
  const supportedLanguages = [...SUPPORTED_TAG_LANGUAGES].filter((lang) => desiredLanguages.includes(lang))

  // desiredLanguagesに基づいてデフォルトタグを生成
  const autoGeneratedTags: Record<SupportedTagLanguage, string[]> = {
    [LanguageType.Japanese]: desiredLanguages.includes(LanguageType.Japanese)
      ? getDefaultTags(category, searchSubGenreIds, LanguageType.Japanese)
      : [],
    [LanguageType.English]: desiredLanguages.includes(LanguageType.English)
      ? getDefaultTags(category, searchSubGenreIds, LanguageType.English)
      : [],
  }

  // 重複タグを削除してからSNSごとの上限を適用する関数
  const getUniqueTags = (tags: string[], limit: number): string[] => {
    const uniqueTags = Array.from(new Set(tags))
    return uniqueTags.slice(0, limit)
  }

  return {
    INSTAGRAM: {
      ...snsRequirements.INSTAGRAM,
      tagList: supportedLanguages.map((lang) => ({
        languageType: lang,
        tags: getUniqueTags(autoGeneratedTags[lang], getSnsTagLimit(CampaignSnsRequirementSnsType.Instagram)),
      })),
    },
    TIKTOK: {
      ...snsRequirements.TIKTOK,
      tagList: supportedLanguages.map((lang) => ({
        languageType: lang,
        tags: getUniqueTags(autoGeneratedTags[lang], getSnsTagLimit(CampaignSnsRequirementSnsType.Tiktok)),
      })),
    },
  }
}
